import { defineBubbleEntry, type AnyBubbleEntry } from './registry';

import { DefaultChatBubble } from '@/components/chat/bubbles/DefaultChatBubble';
import { ExtractedQuestionsBubble } from '@/components/chat/bubbles/ExtractedQuestionsBubble';
import { SharedReferenceView } from '@/components/chat/message-list/SharedReferenceView';
import { resolveMessagePlan } from '@/components/chat/message-list/messagePlan';
import { getMessageReference } from '@/components/chatHelpers';
import { ExtractedStatementsBubble } from '@/features/reference-assistant';
import { shouldShowAddReferencesPrompt } from '@/features/reference-assistant/model';
import { isRecipientContent } from '@/hooks/chat/useMessageLinkOpener';
import { MESSAGE_DATA_TYPES } from '@/types/messages/discriminators';

/**
 * The extracted bubbles select on message *shape*, not on a discriminator —
 * the pipeline writes their fields onto an otherwise ordinary message — so
 * they match through the shared plan rather than a `type` key. Their `type` is
 * an identifier only.
 *
 * `resolveMessagePlan` is pure and deterministic, which is what makes calling
 * it here safe: the row level reaches the same answer from the same inputs
 * without anything being threaded between them.
 */
const extractedQuestionsEntry = defineBubbleEntry({
  type: 'extracted_questions',
  match: (message, ctx) => resolveMessagePlan(message, ctx).showQuestionsBubble,
  visibility: 'both',
  container: { kind: 'bubble', fillRow: true },
  errorBoundary: {
    variant: 'extracted-questions',
    // Degrades to the ordinary bubble, which still carries the message text.
    fallback: 'default',
  },
  toProps: (message, ctx, bubbleProps) => ({
    bubbleProps,
    colors: ctx.colors,
    closedConversation: ctx.closedConversation,
    isAuthor: ctx.isAuthor,
    conversationId: ctx.conversation.id,
    userId: ctx.userId,
    // A caption pairs its label with a content-width bubble.
    shrinkWidthToContent: resolveMessagePlan(message, ctx).isCaptionViewer,
    clinicalCase: message.clinicalCase,
  }),
  Component: ExtractedQuestionsBubble,
});

const extractedStatementsEntry = defineBubbleEntry({
  type: 'extracted_statements',
  match: (message, ctx) => resolveMessagePlan(message, ctx).showStatementsBubble,
  visibility: 'both',
  container: { kind: 'bubble' },
  errorBoundary: { variant: 'extracted-statements', fallback: 'default' },
  toProps: (message, ctx, bubbleProps) => {
    const plan = resolveMessagePlan(message, ctx);

    return {
      bubbleProps,
      colors: ctx.colors,
      closedConversation: ctx.closedConversation,
      isAuthor: ctx.isAuthor,
      conversationId: ctx.conversation.id,
      userId: ctx.userId,
      shrinkWidthToContent: plan.isCaptionViewer,
      linksAreReferences: isRecipientContent(message, ctx.inquiry.recipient?.id),
      // The prompt is this bubble's own affordance. Deciding it here keeps the
      // dispatcher from having to ask which bubble won in order to render a
      // decoration beside it.
      statementReferences: ctx.statementReferences.get(message.messageId),
      showAddReferencesPrompt: shouldShowAddReferencesPrompt({
        state: ctx.statementReferences.get(message.messageId),
        isCurrentUserMessage: plan.isCurrentUserMessage,
        isCaptionViewer: plan.isCaptionViewer,
        isExpertRelayMessage: plan.isExpertRelayMessage,
      }),
    };
  },
  Component: ExtractedStatementsBubble,
});

/**
 * A reference-only message: the whole bubble is the shared reference card.
 *
 * `toProps` returning `null` reproduces `getMessageReference`'s contract — a
 * message typed `reference` whose payload failed to parse falls through to its
 * plain text rather than rendering an empty card. Which viewers see a reference
 * at all is decided upstream in `useChatMessages`, which drops legacy
 * sender-side auto references via `shouldRenderReferenceForViewer`, so there
 * is no visibility gate here.
 */
const referenceEntry = defineBubbleEntry({
  type: MESSAGE_DATA_TYPES.REFERENCE,
  visibility: 'both',
  container: { kind: 'bubble' },
  errorBoundary: { variant: 'shared-reference', fallback: 'default' },
  toProps: (message, ctx, bubbleProps) => {
    const reference = getMessageReference(message);

    if (!reference) {
      return null;
    }

    const plan = resolveMessagePlan(message, ctx);

    const { onLongPress } = bubbleProps;

    return {
      reference,
      // The card replaces gifted-chat's bubble, so it forwards the long press
      // that bubble would have, which is how a reference gets copied or relayed.
      onLongPress: onLongPress
        ? (chatContext: unknown) => onLongPress(chatContext, message)
        : undefined,
      position: plan.position,
      referenceSource: 'suggested' as const,
      // A caption pairs its label with a content-width card, same as the other bubbles.
      shrinkWidthToContent: plan.isCaptionViewer,
    };
  },
  Component: SharedReferenceView,
});

/**
 * The terminal entry: the ordinary bubble every message falls back to.
 *
 * It matches everything, so it must stay last — ordered anywhere else it
 * shadows every entry below it. Its fallback is `null` rather than `'default'`:
 * `'default'` resolves to this same bubble, so a throw would rebuild the thing
 * that just threw and escape the boundary.
 */
const defaultBubbleEntry = defineBubbleEntry({
  type: 'default',
  match: () => true,
  visibility: 'both',
  container: { kind: 'bubble' },
  errorBoundary: { variant: 'default-bubble', fallback: null },
  toProps: (message, ctx, bubbleProps) => ({
    bubbleProps,
    colors: ctx.colors,
    closedConversation: ctx.closedConversation,
    isAuthor: ctx.isAuthor,
    conversationId: ctx.conversation.id,
    userId: ctx.userId,
    shrinkWidthToContent: resolveMessagePlan(message, ctx).isCaptionViewer,
    clinicalCase: message.clinicalCase,
  }),
  Component: DefaultChatBubble,
});

/**
 * Entries that replace the bubble inside an otherwise normal row, tried
 * top-down. The plan already makes the two extracted variants mutually
 * exclusive; the order is belt-and-braces.
 *
 * `defaultBubbleEntry` matches every message and therefore terminates the list.
 */
export const BUBBLE_ENTRIES: readonly AnyBubbleEntry[] = [
  referenceEntry,
  extractedQuestionsEntry,
  extractedStatementsEntry,
  defaultBubbleEntry,
];
